import React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {object, string} from "yup";
import Grid from "@mui/material/Grid2";
import {Info} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle.js";
import SubmitButton from "./layout/forms/SubmitButton.js";
import Loader from "./layout/loading/loader.js";
import ConsigneForm from "./referentiel/consignes/ConsigneForm.js";
import Constants from "../../utils/constants.js";
import {toastHelper} from "../../utils/toast-helper.js";
import {
    useGetConsigneQuery,
    useCreateConsigneMutation,
    useUpdateConsigneMutation,
} from "../../modules/consignes/consignes.api.js";

const consigneValidationSchema = object({
    code: string().required("Le code est requis").max(64, "64 caractères max"),
    libelleFr: string().required("Le libellé FR est requis").max(255, "255 caractères max"),
    libelleAr: string().max(255, "255 caractères max").nullable(),
    picto: string().required("L'icône est requise"),
    couleur: string()
        .required("La couleur est requise")
        .matches(/^#([0-9A-Fa-f]{3}){1,2}$/, "Format de couleur invalide (ex: #FFAA00)"),
});

export default function ConsigneNewOrEdit() {
    const {id} = useParams();
    const mode = id ? "edit" : "create";
    const navigate = useNavigate();

    const {data: consigne, isLoading} = useGetConsigneQuery(id, {skip: !id});
    const [createConsigne] = useCreateConsigneMutation();
    const [updateConsigne] = useUpdateConsigneMutation();

    const initialValues = {
        code: consigne?.code ?? "",
        libelleFr: consigne?.libelleFr ?? "",
        libelleAr: consigne?.libelleAr ?? "",
        picto: consigne?.picto ?? "Info",
        couleur: consigne?.couleur ?? "#11285e",
    };

    const handleSubmit = async (values, {setSubmitting}) => {
        try {
            if (mode === "create") {
                await createConsigne(values).unwrap();
                toastHelper.displaySuccess("La consigne a bien été créée.");
            } else {
                await updateConsigne({id, ...values}).unwrap();
                toastHelper.displaySuccess("La consigne a bien été modifiée.");
            }
            navigate(`${Constants.APP_ROUTE_ADMIN_REFERENTIEL}?tab=consignes`);
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        } finally {
            setSubmitting(false);
        }
    };

    if (id && isLoading) return <Loader/>;

    return (
        <>
            <PageTitle
                title={mode === "create" ? "Nouvelle consigne" : "Modifier la consigne"}
                icon={<Info/>}
            />
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={consigneValidationSchema}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <ConsigneForm {...formik}/>
                        <Grid container justifyContent="center" style={{marginTop: "2rem"}}>
                            <Grid size={4}>
                                <SubmitButton submitting={formik.isSubmitting}/>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>
        </>
    );
}
